/**
 * VersichererDetailScreen - the Versicherer entity-detail page (P2 hardening 02).
 *
 * Route: /versicherer/$id (id = carrier_uid). Übersicht = KPI row + the Stammdaten/CRM card
 * (VersichererStammdaten) + the Ausschreibung card (global placement fields, carrier.manage).
 * Tabs: Übersicht, Policen, Schäden, Rechnungen, Ausschreibungen, Kommunikation. The «Löschen»
 * stub is gone; «Als inaktiv/aktiv markieren» (crm.write) PATCHes the status. Live wiring via
 * useCarrier (header) + useCarrierDetail (Stammdaten + linked counts); mock mode still renders.
 */
import { useState } from 'react';
import { Building2, LayoutDashboard, ShieldCheck, ScrollText, Send, AlertTriangle, ReceiptText, Megaphone, MessageSquare, Power, Bell, FileText, Handshake } from 'lucide-react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/ui/Button';
import { StatusDot } from '../../components/ui/StatusDot';
import { DataTable } from '../../components/data/DataTable';
import { EntityLink } from '../../components/ui/EntityLink';
import { DetailShell } from './components/DetailShell';
import { KpiCard } from './components/KpiCard';
import { FaelligText } from './components/FaelligText';
import { SparteBadge } from './components/SparteBadge';
import { VersichererStammdaten } from './components/VersichererStammdaten';
import { VersichererBenachrichtigungen } from './components/VersichererBenachrichtigungen';
import { POLICY_STATUS_META } from './mock';
import { useCarrier, useCarrierDetail } from '../carriers/hooks/useCarrierRows';
import { usePoliciesForCarrier } from './hooks/useEntityDetail';
import type { Carrier, Policy } from './types';
import { chf } from '../../lib/format';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useCarrierRegistrations } from '../mandate/mandate-registration-view-queries';
import { useEffectiveVisibility } from '../administration/useEffectiveVisibility';
import { VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN } from '@brokly/shared';

export function VersichererDetailScreen() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = useEffectivePermissions();
  const canManage = perms.can('crm.write');
  const live = isLiveData();

  const { carrier, isLoading } = useCarrier(id);
  const { data: detail } = useCarrierDetail(id);
  const { data: policen = [] } = usePoliciesForCarrier(id, carrier?.name ?? '');

  const [activeTab, setActiveTab] = useState('uebersicht');
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  // Linked sub-lists (live only; mock → empty).
  const claims = useQuery({ queryKey: qk.carrierClaims(id), queryFn: () => apiClient.carriers.claims(id), enabled: live });
  const submissions = useQuery({ queryKey: qk.carrierSubmissions(id), queryFn: () => apiClient.carriers.submissions(id), enabled: live });
  const komm = useQuery({ queryKey: qk.carrierKomm(id), queryFn: () => apiClient.carriers.kommunikation(id), enabled: live });
  // F1 (Foundation Slice B): the firm's «Benachrichtigungen» addresses for this carrier.
  const benachr = useQuery({ queryKey: ['carrier-notification-addresses', id], queryFn: () => apiClient.carriers.notificationAddresses(id), enabled: live });
  // The firm's flag «nur manuelle Meldung» (carrier.manage): every Brokermandat Meldung waits for a release.
  const manualReportOnly = useMutation({
    mutationFn: (value: boolean) => apiClient.carriers.registrationSettings(id, { manual_report_only: value }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['carrier-notification-addresses', id] });
    },
  });
  // P4-S7 (step 23): the documents filed at this carrier (Versicherung > Dokumente > Mandate).
  // The route needs document.read, so a role without it does not fire a request that can only 403.
  const docs = useQuery({ queryKey: ['carrier-documents', id], queryFn: () => apiClient.carriers.documents(id), enabled: live && perms.can('document.read') });
  // Kap. 7.7 «spiegelbildlich in der Versicherer-Detailansicht»: the customers registered here (crm.read).
  // The same tab key as the customer's area: a role that hides it neither sees nor reads the mirror.
  const { isHidden } = useEffectiveVisibility();
  const showMandate = perms.can('crm.read') && !isHidden(VISIBILITY_KEY_KUNDEN_MANDATE_EINTRAGUNGEN);
  const mandate = useCarrierRegistrations(id, { enabled: live && showMandate });

  function showFeedback(msg: string) {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(null), 2000);
  }

  if (!carrier) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <p className="text-sm text-muted-foreground">{isLoading ? 'Lade Versicherer …' : 'Versicherer nicht gefunden.'}</p>
        {!isLoading && (
          <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/versicherer' })}>
            Zurück zu Versicherer
          </Button>
        )}
      </div>
    );
  }

  const status = detail?.status ?? carrier.status;

  async function toggleStatus() {
    if (!live) { showFeedback('Nur im Live-Modus'); return; }
    try {
      await apiClient.carriers.update(id, { status: status === 'aktiv' ? 'inaktiv' : 'aktiv' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.carrier(id) }),
        qc.invalidateQueries({ queryKey: qk.carriers('live') }),
      ]);
      showFeedback(status === 'aktiv' ? 'Als inaktiv markiert' : 'Als aktiv markiert');
    } catch (err) {
      showFeedback(err instanceof Error ? err.message : 'Statuswechsel fehlgeschlagen');
    }
  }

  const policyColumns: ColumnDef<Policy>[] = [
    { accessorKey: 'nr', header: 'Policen-Nr.', cell: ({ row }) => <span className="mono-num text-xs font-medium">{row.original.nr}</span> },
    {
      accessorKey: 'kunde', header: 'Kunde',
      cell: ({ row }) => <EntityLink onActivate={() => void navigate({ to: '/customers/$id', params: { id: row.original.kundeId } })}>{row.original.kunde}</EntityLink>,
    },
    { accessorKey: 'sparte', header: 'Sparte', cell: ({ row }) => <SparteBadge sparte={row.original.sparte} /> },
    { accessorKey: 'praemie', header: 'Prämie', cell: ({ row }) => <div className="text-right mono-num">{row.original.praemie === null ? '-' : chf(row.original.praemie)}</div> },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => { const m = POLICY_STATUS_META[row.original.status]; return <StatusDot tone={m.tone} label={m.label} />; } },
    { accessorKey: 'renewalTage', header: 'Renewal', cell: ({ row }) => <FaelligText restTage={row.original.renewalTage} showIcon={false} /> },
  ];

  const linked = detail?.linked;
  const tabs = [
    { value: 'uebersicht', label: 'Übersicht', icon: LayoutDashboard },
    { value: 'policen', label: 'Policen', icon: ShieldCheck, count: linked?.policen ?? policen.length },
    { value: 'schaeden', label: 'Schäden', icon: AlertTriangle, count: linked?.schaeden ?? 0 },
    { value: 'rechnungen', label: 'Rechnungen', icon: ReceiptText, count: linked?.rechnungen ?? 0 },
    { value: 'ausschreibungen', label: 'Ausschreibungen', icon: Megaphone, count: linked?.ausschreibungen ?? 0 },
    { value: 'kommunikation', label: 'Kommunikation', icon: MessageSquare, count: linked?.kommunikation ?? 0 },
    { value: 'benachrichtigungen', label: 'Benachrichtigungen', icon: Bell, count: benachr.data?.items.length ?? 0 },
    ...(showMandate ? [{ value: 'mandate', label: 'Mandate', icon: Handshake, count: mandate.data?.items.length ?? 0 }] : []),
    { value: 'dokumente', label: 'Dokumente', icon: FileText, count: linked?.dokumente ?? docs.data?.items.length ?? 0 },
  ];

  return (
    <div className="flex flex-col gap-0 p-4">
      {feedbackMsg && (
        <div role="status" aria-live="polite" className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md">
          {feedbackMsg}
        </div>
      )}

      <DetailShell
        icon={Building2}
        iconClassName="bg-foreground text-background"
        title={carrier.name}
        statusBlock={status === 'inaktiv' ? <StatusDot tone="neutral" label="Inaktiv" /> : undefined}
        meta={`${linked?.policen ?? carrier.policen} Policen · ${carrier.city ?? 'Ort unbekannt'}${carrier.canton ? ', ' + carrier.canton : ''}`}
        keyNumberLabel="Prämienvolumen"
        keyNumber={chf(carrier.volumen)}
        actions={
          canManage
            ? [{ label: status === 'aktiv' ? 'Als inaktiv markieren' : 'Als aktiv markieren', icon: Power, onClick: () => void toggleStatus() }]
            : []
        }
        backTo={{ label: 'Versicherer', onClick: () => void navigate({ to: '/versicherer' }) }}
        tabs={tabs}
        primaryCount={3}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {activeTab === 'uebersicht' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
              <KpiCard icon={ShieldCheck} label="Policen" value={String(linked?.policen ?? carrier.policen)} />
              <KpiCard icon={ScrollText} label="Prämienvolumen" value={chf(carrier.volumen)} />
              <KpiCard icon={Building2} label="Nächstes Renewal" value={carrier.nextRenewal} />
            </div>
            <VersichererStammdaten uid={id} canManage={canManage} fallback={carrier} />
            <AusschreibungCard carrier={carrier} onSaved={() => showFeedback('Ausschreibungs-Daten gespeichert')} />
          </div>
        )}

        {activeTab === 'policen' && (
          <DataTable columns={policyColumns} data={policen} onRowClick={(p) => void navigate({ to: '/policen/$id', params: { id: p.id } })} />
        )}

        {activeTab === 'schaeden' && (
          <SimpleList
            rows={(claims.data?.items ?? []).map((c) => ({ id: c.id, a: c.policy_number ?? c.id, b: c.kunde_name ?? '-', c: c.status }))}
            headers={['Police', 'Kunde', 'Status']}
            empty="Keine Schäden für diesen Versicherer"
          />
        )}
        {activeTab === 'rechnungen' && (
          <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            {(linked?.rechnungen ?? 0) === 0 ? 'Keine Rechnungen für diesen Versicherer' : `${linked?.rechnungen} Rechnung(en), siehe Rechnungskontrolle`}
          </p>
        )}
        {activeTab === 'ausschreibungen' && (
          <SimpleList
            rows={(submissions.data?.items ?? []).map((s) => ({ id: s.id, a: s.submission_number, b: s.kunde_name ?? '-', c: s.status }))}
            headers={['Nummer', 'Kunde', 'Status']}
            empty="Keine Ausschreibungen für diesen Versicherer"
            onRowClick={(rowId) => void navigate({ to: `/ausschreibungen/${(submissions.data?.items ?? []).find((s) => s.id === rowId)?.placement_id}` as never })}
          />
        )}
        {activeTab === 'kommunikation' && (
          <SimpleList
            rows={(komm.data?.items ?? []).map((k) => ({ id: k.id, a: k.subject ?? '(kein Betreff)', b: k.direction === 'inbound' ? 'Eingang' : 'Ausgang', c: k.channel }))}
            headers={['Betreff', 'Richtung', 'Kanal']}
            empty="Keine Kommunikation für diesen Versicherer"
          />
        )}
        {activeTab === 'benachrichtigungen' && (
          <VersichererBenachrichtigungen
            data={live ? benachr.data : undefined}
            canManage={perms.can('carrier.manage')}
            onToggleManualReportOnly={(value) => manualReportOnly.mutate(value)}
          />
        )}
        {activeTab === 'mandate' && showMandate && (
          <div className="flex flex-col gap-2" data-testid="versicherer-mandate">
            {mandate.isError ? (
              <p className="text-sm text-destructive" role="alert" data-testid="versicherer-mandate-error">
                Die Mandate dieses Versicherers konnten nicht geladen werden.
              </p>
            ) : mandate.data === undefined ? (
              <p className="text-sm text-muted-foreground" role="status" data-testid="versicherer-mandate-loading">
                Lädt …
              </p>
            ) : mandate.data.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Kunden mit einer Meldung bei diesem Versicherer.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1">Kunde</th>
                    <th className="py-1">Kundennummer</th>
                    <th className="py-1">Aktive Policen</th>
                    <th className="py-1">Analysemandat</th>
                    <th className="py-1">Brokermandat</th>
                  </tr>
                </thead>
                <tbody>
                  {(mandate.data?.items ?? []).map((c) => (
                    <tr key={`${c.subject_type}:${c.subject_id}`} className="border-t" data-testid={`versicherer-mandate-${c.subject_id}`}>
                      <td className="py-1">
                        <EntityLink
                          onActivate={() => void navigate({ to: '/kunden/$id', params: { id: c.subject_id }, search: { tab: 'mandate_eintragungen' } })}
                        >
                          {c.display_name ?? c.customer_number ?? c.subject_id}
                        </EntityLink>
                      </td>
                      <td className="py-1 mono-num text-xs">{c.customer_number ?? '-'}</td>
                      <td className="py-1 mono-num">{c.active_policy_count}</td>
                      <td className="py-1">{c.analyse?.status_label ?? '-'}</td>
                      <td className="py-1">{c.broker?.status_label ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {activeTab === 'dokumente' && (
          <SimpleList
            rows={(docs.data?.items ?? []).map((d) => ({
              id: d.document_id,
              a: d.filename,
              b: d.kind ?? d.type ?? '-',
              c: d.created_at.slice(0, 10),
            }))}
            headers={['Dokument', 'Art', 'Abgelegt']}
            empty="Keine Dokumente für diesen Versicherer"
          />
        )}
      </DetailShell>
    </div>
  );
}

/** A minimal 3-column read list for the linked-object tabs. */
function SimpleList({
  rows,
  headers,
  empty,
  onRowClick,
}: {
  rows: Array<{ id: string; a: string; b: string; c: string }>;
  headers: [string, string, string];
  empty: string;
  onRowClick?: (id: string) => void;
}) {
  if (rows.length === 0) {
    return <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  const columns: ColumnDef<{ id: string; a: string; b: string; c: string }>[] = [
    { accessorKey: 'a', header: headers[0] },
    { accessorKey: 'b', header: headers[1] },
    { accessorKey: 'c', header: headers[2], cell: ({ row }) => <span className="text-muted-foreground">{row.original.c}</span> },
  ];
  return <DataTable columns={columns} data={rows} onRowClick={onRowClick ? (r) => onRowClick(r.id) : undefined} />;
}

/** Badge label for an effective placement value's source. */
function sourceBadge(source: 'tenant' | 'global' | null): string {
  return source === 'tenant' ? 'verwendet: eigene' : source === 'global' ? 'verwendet: Standard' : 'verwendet: keine';
}

/**
 * AusschreibungCard - THIS firm's OWN per-insurer placement contacts (Ausschreibungs-E-Mail,
 * Antragsformular) on the tenant `versicherer` overlay (PR#43-B1). Edited by `carrier.manage`; an
 * empty input saves as NULL = back to the Brokly-Standard (the platform-curated global default).
 * The logo is global, read-only here (curated by Brokly via the admin surface).
 */
function AusschreibungCard({ carrier, onSaved }: { carrier: Carrier; onSaved: () => void }) {
  const perms = useEffectivePermissions();
  const qc = useQueryClient();
  const canManage = perms.can('carrier.manage');
  const live = isLiveData();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(carrier.emailSubmissionOwn ?? '');
  const [tmpl, setTmpl] = useState(carrier.formTemplateRefOwn ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (live) {
        // Only the firm's own two fields — logo_url is global, not sent.
        await apiClient.carriers.patchPlacementSettings(carrier.id, {
          email_submission: email.trim() === '' ? null : email.trim(),
          form_template_ref: tmpl.trim() === '' ? null : tmpl.trim(),
        });
        await qc.invalidateQueries({ queryKey: qk.carriers('live') });
      }
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="versicherer-ausschreibung">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Send className="size-4 text-muted-foreground" aria-hidden /> Ausschreibung
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Eigene Kontaktdaten dieser Firma für diesen Versicherer</p>
        </div>
        {canManage &&
          (editing ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Abbrechen</Button>
              <Button size="sm" onClick={() => void save()} disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="versicherer-ausschreibung-edit">Bearbeiten</Button>
          ))}
      </div>

      {error !== null && <p className="mt-2 text-sm text-destructive">{error}</p>}

      <div className="mt-3 flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Ausschreibungs-E-Mail (eigene)</span>
          {editing ? (
            <input type="email" value={email} disabled={saving} onChange={(e) => setEmail(e.target.value)} aria-label="Ausschreibungs-E-Mail (eigene)" placeholder="Leer = Brokly-Standard" className="rounded-md border bg-background px-2 py-1 text-sm" />
          ) : (
            <span className="text-sm text-foreground">{carrier.emailSubmission ?? '-'}</span>
          )}
          <span className="text-xs text-muted-foreground">Brokly-Standard: {carrier.emailSubmissionGlobal ?? '-'}</span>
          <span className="text-xs text-muted-foreground" data-testid="versicherer-email-source">{sourceBadge(carrier.emailSubmissionSource)}</span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Antragsformular-Template (eigenes)</span>
          {editing ? (
            <input value={tmpl} disabled={saving} onChange={(e) => setTmpl(e.target.value)} aria-label="Antragsformular-Template (eigenes)" placeholder="Leer = Brokly-Standard" className="rounded-md border bg-background px-2 py-1 text-sm" />
          ) : (
            <span className="text-sm text-foreground">{carrier.formTemplateRef ?? '-'}</span>
          )}
          <span className="text-xs text-muted-foreground">Brokly-Standard: {carrier.formTemplateRefGlobal ?? '-'}</span>
          <span className="text-xs text-muted-foreground" data-testid="versicherer-tmpl-source">{sourceBadge(carrier.formTemplateRefSource)}</span>
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Logo</span>
          <span className="truncate text-sm text-foreground">{carrier.logoUrl ?? '-'}</span>
          <span className="text-xs text-muted-foreground">Logo wird zentral von Brokly gepflegt</span>
        </div>
      </div>
    </div>
  );
}
